// /admin/images — central directory of every image slot on the site.
//
// Each entry is a *declared* slot the site can render. `/admin/images`
// uses this list to group, search, and surface every image — even ones
// that have not been overridden yet (so editors see the brand default
// rendered on the page and can replace it inline).
//
// The actual override state lives in MongoDB `image_slots` collection
// and is fetched at runtime via `/api/slots`. Slots from the DB that
// aren't declared here still appear in the directory under
// "Unregistered" so nothing falls through the cracks.

import { HOTELS } from "./data";

// ── Lookup helpers ─────────────────────────────────────────────────────
const hotelBySlug = (slug) => HOTELS.find((h) => h.slug === slug);

// ── Hotel-slug derivation ──────────────────────────────────────────────
//
// Many slots (booking thumbs, contact thumbs, ecosystem cards, etc.)
// are tied to ONE specific hotel even though they render on cross-
// cutting pages. We derive the hotel slug from the slot id so each
// hotel gets a dedicated dashboard in /admin/images that pulls *every*
// slot related to it — wherever it surfaces on the public site.
const HOTEL_SLUG_PATTERNS = [
  /^hotel\/([^/]+)\//,
  /^cms\.hotel\.([^.]+)\./,
  /^cms\.hotel-ecosystem\.([^.]+)\./,
  /^cms\.experience-related-hotel\.([^.]+)\./,
  /^cms\.service\.([^.]+)\./,
  /^cms\.bivouac-card\.([^.]+)\./,
  /^cms\.refuge-collection\.([^.]+)\./,
  /^cms\.bivouacs-camp-chip\.([^.]+)\./,
  /^cms\.booking-hotel-card\.([^.]+)\./,
  /^cms\.contact-property\.([^.]+)\./,
  /^cms\.pricing-property\.([^.]+)\./,
  /^cms\.hotels-index\.([^.]+)\./,
  /^cms\.activity\.([^.]+)\./,
];
const deriveHotelSlug = (slot) => {
  if (!slot) return null;
  for (const re of HOTEL_SLUG_PATTERNS) {
    const m = slot.match(re);
    if (m && hotelBySlug(m[1])) return m[1];
  }
  return null;
};

// ── 1. Static slots already in use across pages/components ─────────────
//
// We map each known slot id to its page (URL where it renders), the
// section it belongs to on that page, a human-readable label, and the
// hardcoded fallback URL the code uses if the slot isn't overridden.
//
// Slots are listed in display order — the directory groups them by page
// first, section second.
//
// The optional `fallbackKind` field categorises where the fallback URL
// physically lives. It drives the "Needs migration" indicator on
// `/admin/images`:
//
//   • "brand"    — the fallback bytes live in Grup Xaluca's own
//                  storage (Bunny CDN, Emergent Object Storage or
//                  frontend/public bundle). Safe long-term default.
//   • "external" — the fallback bytes live in a third party
//                  (Unsplash, Pexels, customer-assets.emergentagent
//                  .com, etc). Renders fine today but should be
//                  editorially replaced. Surfaces as
//                  `Needs migration` in /admin/images so the editor
//                  can filter and work through the list.
//   • undefined  — legacy entries whose fallback is null or was set
//                  before this classification existed. Treated as
//                  "brand default" until reviewed.
const STATIC_SLOTS = [
  // ── Homepage ──────────────────────────────────────────────────────────
  {
    slot: "home.hero.bg",
    page: "/",
    section: "Hero",
    label: "Main hero — full-screen background",
    description: "The primary cinematic image visible the moment a guest lands on visitxaluca.com. Used as the full-bleed hero on the homepage.",
    fallback: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/zg40h47b_Kasbah%20Hotel%20Xaluca%20-%20Habitaciones%20-%20Imagen8.jpg",
    fallbackKind: "external",
  },
  // ── Homepage · Hero slide 0 (video intro) ────────────────────────────
  {
    slot: "cms.home.hero.slide.0.image",
    page: "/",
    section: "Hero — Slide 1 (video intro)",
    label: "Hero — Slide 1 · Fallback image",
    description: "Full-bleed image that opens the homepage — used as the poster of the Mux brand film on mobile and as the still fallback while the video is loading. Should be a wide, cinematic shot representative of the whole collection.",
    fallback: "https://static.prod-images.emergentagent.com/jobs/4e06df6f-9a63-48ff-b8e2-384249cc96cb/images/3ef3804f701debb6deb08b44c397ef614c4c4d52fd2e912b5f5cbb24b2b0703e.png",
    fallbackKind: "external",
  },
  // ── Homepage · Hero social proof avatars ─────────────────────────────
  ...[0, 1, 2, 3].map((i) => ({
    slot: `cms.home.hero.social-proof.avatar.${i}`,
    page: "/",
    section: "Hero — Social proof",
    label: `Hero — Guest avatar ${i + 1}`,
    description: `Portrait ${i + 1} of the four small circular avatars that sit between the sub-headline and the CTAs on the homepage hero. Should be a real guest — face-cropped 160×160 minimum.`,
    // Bytes live in Bunny CDN (via /pexels/ path) but the *content*
    // is still a stock Pexels photo mirrored through our CDN. Flag as
    // external so the editor is nudged to replace with real guest
    // portraits.
    fallback: `https://cdn.visitxaluca.com/pexels/photos/${["2379004", "1181686", "1239291", "1043471"][i]}/pexels-photo-${["2379004", "1181686", "1239291", "1043471"][i]}.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop&crop=faces`,
    fallbackKind: "external",
  })),
  // ── Homepage · Originals video posters (VideoSection scope) ──────────
  {
    slot: "cms.home-videos.video.feat.poster",
    page: "/",
    section: "Videos — Featured film",
    label: "Videos — Featured film poster",
    description: 'Poster of the featured brand film ("The Grup Xaluca universe — south of Morocco in two minutes"). Renders as the large 2/3-width card of the "Films of the south" section.',
    fallback: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
    fallbackKind: "external",
  },
  {
    slot: "cms.home-videos.video.bivouacs.poster",
    page: "/",
    section: "Videos — Bivouacs film",
    label: "Videos — Bivouacs film poster",
    description: 'Poster of the "A night under the dunes" short film. Small card, top-right of the "Films of the south" section.',
    fallback: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
    fallbackKind: "external",
  },
  {
    slot: "cms.home-videos.video.gastronomy.poster",
    page: "/",
    section: "Videos — Gastronomy film",
    label: "Videos — Gastronomy film poster",
    description: 'Poster of the "Inside the kitchens of Arfoud" short film. Small card, middle-right of the "Films of the south" section.',
    fallback: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1600&q=80",
    fallbackKind: "external",
  },
  {
    slot: "cms.home-videos.video.experiences.poster",
    page: "/",
    section: "Videos — Experiences film",
    label: "Videos — Experiences film poster",
    description: 'Poster of the "A 4×4 day across the Hammada" short film. Small card, bottom-right of the "Films of the south" section.',
    fallback: "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
    fallbackKind: "external",
  },
  // ── Homepage · Bivouacs section ──────────────────────────────────────
  {
    slot: "cms.home.bivouacs.dunes-bg",
    page: "/",
    section: "Bivouacs — Section background",
    label: "Bivouacs — Dune background",
    description: 'Full-bleed silhouette of dunes that sits at the bottom of the "Bivouacs & Desert Camps" homepage section, behind the property cards.',
    fallback: "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/vj5kdsqu_Background%20Dunes.png",
    fallbackKind: "external",
  },
  // ── Homepage · Heritage gallery ──────────────────────────────────────
  {
    slot: "cms.home.heritage.gallery.heritage.0",
    page: "/",
    section: "Heritage — Photo carousel",
    label: "Heritage — Opening photograph",
    description: 'First photograph of the "Our Heritage" carousel on the homepage. Sets the tone for the founder story — favour a black-and-white or sepia-toned image of the family or the kasbah.',
    fallback: "https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1600",
    fallbackKind: "external",
  },
];

// ── 2. WhyBookWithUs grid (6 fixed reason tiles on homepage) ───────────
const WHY_BOOK_REASONS = [
  { key: "family", label: "Reason — Family-run hospitality" },
  { key: "desert", label: "Reason — Desert specialists" },
  { key: "geography", label: "Reason — Geographic reach" },
  { key: "concierge", label: "Reason — Personal concierge" },
  { key: "local", label: "Reason — Local team & culture" },
  { key: "rituals", label: "Reason — Signature rituals" },
];

WHY_BOOK_REASONS.forEach((r) => {
  STATIC_SLOTS.push({
    slot: `home.why-book.image.${r.key}`,
    page: "/",
    section: "Why book with us",
    label: r.label,
    description: `Background image for the “${r.key}” reason card in the “Why book with us” section.`,
    fallback: null,
  });
});

// ── 3. Homepage collection cards — one per hotel (14 × 10 thumbnails) ─
// Each hotel card on the homepage cycles through up to 10 images. The
// SAME slot drives both the big main image AND the corresponding small
// thumbnail underneath — there is NO separate "featured" slot. Editors
// can also manage these from /admin/galleries → "Hotel card images".
HOTELS.forEach((h) => {
  for (let i = 0; i < 10; i += 1) {
    STATIC_SLOTS.push({
      slot: `cms.hotel.${h.slug}.thumb.${i}`,
      page: "/",
      section: "Property collection cards",
      label: i === 0 ? `Card image 1 (main) — ${h.name}` : `Card image ${i + 1} — ${h.name}`,
      description: i === 0
        ? `First image on the homepage property card for ${h.name}. Doubles as the main image AND the first thumbnail — they always stay in sync.`
        : `Thumbnail ${i + 1} on the homepage property card for ${h.name}. Becomes the main image when a visitor clicks it.`,
      fallback: i === 0 ? h.image : null,
      relatedAdmin: { href: "/admin/galleries", label: "Manage all 10 thumbnails together in /admin/galleries" },
    });
  }
});

// ── 4. Hotel detail — per-hotel CMS overlays wired in HotelDetailPage.jsx ─
//
// The page renders 5 recurring grids of `<EditableImage>` cards. Each
// card computes its slot as `hotel.<slug>.<grid>.<index>.thumb` (dots,
// zero-indexed) and its fallback via `pickThumb(section, i, ownImage)`
// which returns a Pexels/Unsplash URL from the `THUMB_DEFAULTS` map.
//
// We declare every possible slot so /admin/images can surface them as
// "Needs migration" until editors upload real Grup Xaluca imagery.
// Counts match the on-page ceiling; extra items would render as extra
// registry slots plus AutoImageEditor huérfanos.
const HOTEL_THUMB_GRIDS = [
  { key: "services",     count: 8, label: "Services thumb" },
  { key: "excursions",   count: 8, label: "Excursions thumb" },
  { key: "destination",  count: 8, label: "Destinations thumb" },
  { key: "restaurants",  count: 6, label: "Restaurants thumb" },
  { key: "events",       count: 6, label: "Events thumb" },
];

HOTELS.forEach((h) => {
  HOTEL_THUMB_GRIDS.forEach((g) => {
    for (let i = 0; i < g.count; i++) {
      STATIC_SLOTS.push({
        slot: `hotel.${h.slug}.${g.key}.${i}.thumb`,
        page: `/hotels/${h.slug}`,
        section: `Hotel detail — ${g.key}`,
        label: `${h.name} — ${g.label} #${i + 1}`,
        description: `Per-card thumbnail rendered in the "${g.key}" grid on the ${h.name} detail page. Falls back to a Pexels/Unsplash rotation from THUMB_DEFAULTS until a real Grup Xaluca image is uploaded.`,
        fallback: null,
        // The runtime fallback comes from THUMB_DEFAULTS (Pexels/
        // Unsplash) so surface these as `Needs migration` until the
        // editor swaps in owned imagery.
        fallbackKind: "external",
        relatedAdmin: { href: "/admin/galleries", label: "Manage room galleries in /admin/galleries" },
        hotelSlug: h.slug,
      });
    }
  });
});

// ── 4b. Per-hotel hero slide slots ──────────────────────────────────────
//
// HotelDetailPage.jsx uses <HeroSlider scope={`hotel.${slug}`} …/> so
// every hero slide is captured as `cms.hotel.<slug>.slide.<i>.image`.
// The DB shows properties commonly have 3–10 slides. Cap at 10 to
// prevent runaway registry growth.
HOTELS.forEach((h) => {
  for (let i = 0; i < 10; i += 1) {
    STATIC_SLOTS.push({
      slot: `cms.hotel.${h.slug}.slide.${i}.image`,
      page: `/hotels/${h.slug}`,
      section: `Hotel detail — Hero carousel`,
      label: `${h.name} — Hero slide ${i + 1}`,
      description: `Full-bleed image for slide ${i + 1} of the hotel-detail hero carousel. First slide is the main property still; subsequent slides tell the location story.`,
      fallback: i === 0 ? h.image : null,
      // h.image comes from HOTELS[i].image in data.js which is a
      // Pexels URL for every property today — external.
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
  }
});

// ── 4c. Per-bivouac camp images (la-belle-etoile 3-camp breakdown) ──────
//
// Only la-belle-etoile-bivouacs currently exposes multiple camps.
// Each camp renders as a card with slot `cms.hotel.<slug>.camp.<camp-slug>.image`.
const LA_BELLE_CAMPS = [
  { slug: "bivouac-la-belle-etoile",  label: "Traditional Sahrawi bivouac" },
  { slug: "luxury-desert-camp",       label: "Luxury desert camp (flagship)" },
  { slug: "luxury-desert-camp-mini",  label: "Luxury desert camp (mini)" },
];
LA_BELLE_CAMPS.forEach((c) => {
  STATIC_SLOTS.push({
    slot: `cms.hotel.la-belle-etoile-bivouacs.camp.${c.slug}.image`,
    page: "/hotels/la-belle-etoile-bivouacs",
    section: "Hotel detail — Camp breakdown",
    label: `La Belle Étoile — ${c.label} · cover image`,
    description: `Cover image for the "${c.label}" camp card on /hotels/la-belle-etoile-bivouacs.`,
    fallback: null,
    fallbackKind: "external",
    hotelSlug: "la-belle-etoile-bivouacs",
  });
});

// ── 5. Hero slide slots — every page using <PageHero scope="page.X"> ──
//
// PageHero (since iter 28) emits ONE editable slot per page-specific
// image — `cms.<scope>.slide.<i>.image` for i ∈ [0, images.length-1].
// All historical pages pass 3 images, so each page exposes 3 hero slots.
// The brand-wide Mux video slide that used to lead every hero was
// removed (it broke "every page has its own hero" and was non-editable).
const HERO_PAGES = [
  { scope: "page.about",            page: "/about",            label: "About",             slides: 3 },
  { scope: "page.bivouacs",         page: "/bivouacs",         label: "Bivouacs",          slides: 3 },
  { scope: "page.blog",             page: "/blog",             label: "Blog",              slides: 3 },
  { scope: "page.booking",          page: "/booking",          label: "Booking",           slides: 3 },
  { scope: "page.contact",          page: "/contact",          label: "Contact",           slides: 3 },
  { scope: "page.events",           page: "/events",           label: "Events",            slides: 3 },
  { scope: "page.experiences",      page: "/experiences",      label: "Experiences",       slides: 3 },
  { scope: "page.gastronomy",       page: "/gastronomy",       label: "Gastronomy",        slides: 3 },
  { scope: "page.packs",            page: "/packs",            label: "Packs",             slides: 3 },
  { scope: "page.press",            page: "/press",            label: "Press",             slides: 3 },
  { scope: "page.pricing",          page: "/pricing",          label: "Pricing",           slides: 3 },
  { scope: "page.southern-morocco", page: "/southern-morocco", label: "Southern Morocco",  slides: 3 },
  { scope: "page.trips",            page: "/trips",            label: "Trips",             slides: 3 },
  { scope: "page.wall-of-love",     page: "/wall-of-love",     label: "Wall of love",      slides: 3 },
  { scope: "page.work-with-us",     page: "/work-with-us",     label: "Work with us",      slides: 3 },
];

HERO_PAGES.forEach((p) => {
  for (let i = 0; i < p.slides; i++) {
    STATIC_SLOTS.push({
      slot: `cms.${p.scope}.slide.${i}.image`,
      page: p.page,
      section: "Hero slider",
      label: `${p.label} hero — slide #${i + 1}`,
      description: `Slide #${i + 1} of the ${p.label} hero on the public page. Editable independently per page; updating it never affects other pages.`,
      fallback: null,
    });
  }
});

// ── 6. Page-card slots — tiles wired in fase 2 of the migration ──────
//
// Each surface below was migrated to use `data-cms-scope` + `data-cms-role`
// so the AutoImageEditor emits stable slot ids like
// `cms.<scope>.<role>`. The pages are listed here so editors can find
// them in /admin/images without first having to land on the page.

// Southern Morocco — 9 region tiles
const SM_REGIONS = ["merzouga", "rissani", "arfoud", "todgha", "dades", "boumalne", "roses", "ouarzazate", "aitbenhaddou"];
SM_REGIONS.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.region.${id}.card.image`,
    page: "/southern-morocco",
    section: "Region tiles",
    label: `${id.charAt(0).toUpperCase() + id.slice(1)} — region tile`,
    description: `Editorial photo paired with the ${id} region card on the Southern Morocco page.`,
    fallback: null,
  });
});

// Group trips — 6 category tiles
const TRIP_KEYS = ["desert", "atlas", "cultural", "coastal", "imperial", "grand"];
TRIP_KEYS.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.trip-category.${id}.card.image`,
    page: "/trips",
    section: "Trip category tiles",
    label: `${id.charAt(0).toUpperCase() + id.slice(1)} — trip category cover`,
    description: `Cover image rendered above the ${id} trip-category card.`,
    fallback: null,
  });
});

// Events — 6 opportunity tiles + 2 region tiles (desert + atlas) + 4 deep-dive
// tiles + 1 conference-room hero + 1 final-cta backdrop. Real ids match the
// `data-cms-scope` strings emitted by EventsPage.jsx.
for (let i = 0; i < 6; i++) {
  STATIC_SLOTS.push({
    slot: `cms.events.opportunity.${i}.card.image`,
    page: "/events",
    section: "Events — opportunity tiles",
    label: `Opportunity tile #${i + 1}`,
    description: `Vertical 4:5 tile in the “Event opportunities” grid on the Events page.`,
    fallback: null,
  });
}
["desert", "atlas"].forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.events.region.${id}.card.image`,
    page: "/events",
    section: "Events — regional highlights",
    label: `Regional highlight — ${id}`,
    description: `Big 4:3 editorial photo for the “${id}” regional highlight on the Events page.`,
    fallback: null,
  });
});
const EVENTS_DEEP_IDS = ["festivals", "sporting", "business", "celebrations"];
EVENTS_DEEP_IDS.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.events.deep.${id}.card.image`,
    page: "/events",
    section: "Events — deep dives",
    label: `Deep dive — ${id}`,
    description: `Editorial 4:3 photo paired with the “${id}” deep-dive block on the Events page.`,
    fallback: null,
  });
});
// Standalone editorial blocks (conference room highlight + final CTA backdrop)
STATIC_SLOTS.push({
  slot: "cms.events.conference-room.hero",
  page: "/events",
  section: "Events — conference room",
  label: "Conference room — hero",
  description: "4:3 hero showing the conference room paired with the “best conference room in southern Morocco” editorial block.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.events.final-cta.bg",
  page: "/events",
  section: "Events — final CTA",
  label: "Final CTA — backdrop",
  description: "Full-bleed dim backdrop behind the “Get in touch” final call-to-action at the bottom of the Events page.",
  fallback: null,
});

// Gastronomy — 3 pillars + 6 venues
for (let i = 0; i < 3; i++) {
  STATIC_SLOTS.push({
    slot: `cms.gastronomy.pillar.${i}.card.image`,
    page: "/gastronomy",
    section: "Gastronomy — pillars",
    label: `Pillar #${i + 1}`,
    description: `Vertical 4:5 photo paired with the pillar narrative on the Gastronomy page.`,
    fallback: null,
  });
}
for (let i = 0; i < 6; i++) {
  STATIC_SLOTS.push({
    slot: `cms.gastronomy.venue.${i}.card.image`,
    page: "/gastronomy",
    section: "Gastronomy — venues",
    label: `Venue tile #${i + 1}`,
    description: `Vertical 4:5 tile in the Gastronomy venues grid.`,
    fallback: null,
  });
}

// Wall of love — 16 testimonials + 3 stories + 3 videos
for (let i = 1; i <= 16; i++) {
  STATIC_SLOTS.push({
    slot: `cms.testimonial.${i}.avatar`,
    page: "/wall-of-love",
    section: "Wall of love — testimonials",
    label: `Testimonial #${i} — header image`,
    description: `16:9 header photo on the testimonial card #${i} on the Wall of love page.`,
    fallback: null,
  });
}
[1, 2, 3].forEach((i) => {
  STATIC_SLOTS.push({
    slot: `cms.wol-story.story-${i}.card.image`,
    page: "/wall-of-love",
    section: "Wall of love — stories",
    label: `Story #${i} cover`,
    description: `4:3 cover image for the long-form story card #${i} on the Wall of love page.`,
    fallback: null,
  });
  STATIC_SLOTS.push({
    slot: `cms.wol-video.vid-${i}.poster`,
    page: "/wall-of-love",
    section: "Wall of love — videos",
    label: `Video #${i} poster`,
    description: `4:5 poster image for the video testimonial card #${i}.`,
    fallback: null,
  });
});

// Blog — 4 categories + 8 posts + 4 team
const BLOG_CATEGORY_KEYS = ["morocco", "hotels", "events", "bivouacs"];
BLOG_CATEGORY_KEYS.forEach((key) => {
  STATIC_SLOTS.push({
    slot: `cms.blog-category.${key}.card.image`,
    page: "/blog",
    section: "Blog — category tiles",
    label: `Blog category — ${key}`,
    description: `Cover image for the ${key} category card on the Blog index.`,
    fallback: null,
  });
});
const BLOG_POST_SLUGS = [
  "stargazing-erg-chebbi",
  "tayeb-and-lluis-thirty-five-years",
  "the-grammar-of-pise",
  "before-the-souk-rissani-at-sunrise",
  "weddings-at-mabroka",
  "into-the-m-goun",
  "the-table-at-sijilmassa",
  "stories-from-tayeb",
];
BLOG_POST_SLUGS.forEach((slug) => {
  STATIC_SLOTS.push({
    slot: `cms.blog-post.${slug}.featured-image`,
    page: "/blog",
    section: "Blog — post covers",
    label: `Post cover — ${slug}`,
    description: `Featured 4:3 (or 16:10 if hero) cover image for the blog post “${slug}”.`,
    fallback: null,
  });
});
const BLOG_TEAM_IDS = ["tayeb", "lluis", "khadija", "lahcen"];
BLOG_TEAM_IDS.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.blog-team.${id}.avatar`,
    page: "/blog",
    section: "Blog — team",
    label: `Team avatar — ${id}`,
    description: `Round 56×56 avatar of ${id} in the Blog “human” section.`,
    fallback: null,
  });
});

// Press — 6 article tiles
const PRESS_ARTICLES = ["conde-nast-2024", "monocle-2024", "nyt-2023", "vogue-2023", "telegraph-2023", "elle-deco-2022"];
PRESS_ARTICLES.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.press-article.${id}.card.image`,
    page: "/press",
    section: "Press — article tiles",
    label: `Press article — ${id}`,
    description: `16:10 editorial photo for the press article card “${id}”.`,
    fallback: null,
  });
});

// ── Fase 3.5 — slots wired during the page-by-page audit ──────────────

// Xaluca Originals carousel — secondary EXPERIENCES (`secondary: true`)
// rendered as a swipeable carousel on the homepage. Slots per card.
const XALUCA_ORIGINAL_KEYS = ["sunrise-camel-trek", "sunset-camel-trek", "arfoud-pizza-night", "quad-erg-chebbi-2h", "quad-erg-chebbi-4h"];
XALUCA_ORIGINAL_KEYS.forEach((key) => {
  STATIC_SLOTS.push({
    slot: `cms.xaluca-original.${key}.card.image`,
    page: "/",
    section: "Homepage — Xaluca Originals carousel",
    label: `Original card — ${key}`,
    description: `16:10 cover image in the “Xaluca Originals” swipeable carousel on the homepage.`,
    fallback: null,
  });
});

// Hotel detail — per-hotel director portrait + ecosystem cards
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.hotel.${h.slug}.director.portrait`,
    page: `/hotels/${h.slug}`,
    section: "Hotel detail — director's message",
    label: `${h.name} — director portrait`,
    description: `4:5 portrait of the director paired with their personal letter on the ${h.name} detail page.`,
    fallback: null,
  });
  STATIC_SLOTS.push({
    slot: `cms.hotel-ecosystem.${h.slug}.card.image`,
    page: "/hotels/*",
    section: "Hotel ecosystem cards",
    label: `Ecosystem card — ${h.name}`,
    description: `3:4 thumbnail rendered in the “Explore the rest of the ecosystem” block on every other hotel detail page.`,
    fallback: h.image,
  });
  STATIC_SLOTS.push({
    slot: `cms.experience-related-hotel.${h.slug}.card.image`,
    page: "/experiences/*",
    section: "Experience detail — related hotels",
    label: `Related hotel — ${h.name}`,
    description: `3:4 thumbnail rendered in the “Stay at one of these properties” block at the bottom of every experience detail page.`,
    fallback: h.image,
  });
});

// Service detail pages — hero per (hotel × service) pair.
// Hotels carry a `services` array in hotelDetails.js. Slots per pair
// are surfaced lazily; the registry just declares the prefix so editors
// know where they'll show up.
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.service.${h.slug}.<service>.hero`,
    page: `/hotels/${h.slug}/services/<service>`,
    section: "Service detail — hero (prefix)",
    label: `${h.name} — service hero (any service slug)`,
    description: `Full-bleed hero at the top of any /hotels/${h.slug}/services/<service> page. The “<service>” placeholder resolves to the service slug (e.g. bars-restaurants, spa-wellness). Edit a specific service hero from the public page in Edit mode or via this directory once the slot has been created.`,
    fallback: null,
    pending: true,
  });
});

// HomePage — refuge collection card lead image — already declared, but
// the `<picture>` fix may surface previously stuck overrides. No change
// here, the existing entry covers it.

// Discovery / brand marks already registered globally — nothing new.

// Partners — hero bg + about image + network image
[
  { slot: "cms.partners.hero.bg",          page: "/partners", section: "Partners — hero",     label: "Partners — hero background", description: "Full-bleed hero image at the top of the Partners page." },
  { slot: "cms.partners.about.image",      page: "/partners", section: "Partners — about",    label: "Partners — about photo",     description: "3:4 portrait image paired with the about block on the Partners page." },
  { slot: "cms.partners.network.image",    page: "/partners", section: "Partners — network",  label: "Partners — network photo",   description: "4:3 image paired with the “Our network” block on the Partners page." },
].forEach((s) => STATIC_SLOTS.push({ ...s, fallback: null }));

// Bivouacs page — per-bivouac hero + 4 gallery thumbs (uses HOTELS bivouac category slugs)
HOTELS.filter((h) => h.category === "bivouac").forEach((b) => {
  STATIC_SLOTS.push({
    slot: `cms.bivouac-card.${b.slug}.hero`,
    page: "/bivouacs",
    section: "Bivouacs — hero per camp",
    label: `${b.name} — main hero`,
    description: `16:10 lead photo for ${b.name} on the Bivouacs landing page.`,
    fallback: null,
  });
  for (let i = 0; i < 4; i++) {
    STATIC_SLOTS.push({
      slot: `cms.bivouac-card.${b.slug}.thumb.${i}`,
      page: "/bivouacs",
      section: "Bivouacs — gallery thumbs",
      label: `${b.name} — thumb ${i + 1}`,
      description: `4:3 gallery thumbnail #${i + 1} below the main image for ${b.name}.`,
      fallback: null,
    });
  }
});

// About — 2 founder portraits
["tayeb", "lluis"].forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.founder.${id}.portrait`,
    page: "/about",
    section: "About — founders",
    label: `Founder portrait — ${id}`,
    description: `4:5 portrait of ${id} on the founders grid of the About page.`,
    fallback: null,
  });
});

// About — 9 timeline polaroids. Each polaroid represents a milestone
// property; the slot id follows `cms.about.timeline.<slug>.image` which
// matches the `scope` prop passed to `<MoroccanPolaroid>` in
// AboutPage.jsx.
const ABOUT_TIMELINE_SLUGS = [
  "kasbah-hotel-tombouctou",
  "kasbah-hotel-xaluca-arfoud",
  "auberge-la-belle-etoile",
  "hotel-xaluca-dades",
  "xaluca-ifess-fess-refuge",
  "xaluca-ait-ben-haddou",
  "hotel-xaluca-spa-aguelmame-sidi-ali",
  "hotel-riad-xaluca",
  "hotel-xaluca-dunes",
];
ABOUT_TIMELINE_SLUGS.forEach((slug) => {
  STATIC_SLOTS.push({
    slot: `cms.about.timeline.${slug}.image`,
    page: "/about",
    section: "About — Timeline polaroids",
    label: `About timeline — ${slug}`,
    description: `Polaroid photograph on the /about timeline for the ${slug} milestone. Rendered by <MoroccanPolaroid scope="about.timeline.${slug}">.`,
    fallback: null,
    fallbackKind: "external",
    hotelSlug: slug,
  });
});

// Home — Heritage Timeline carousel (11 milestones). One card per
// entry in TIMELINE[]. Each card renders an <img> tagged with
// data-cms-role="card.image" inside an ancestor
// data-cms-scope="heritage-timeline.<year>-<slug>" so the slot id
// `cms.heritage-timeline.<year>-<slug>.card.image` stays stable
// across DOM refactors and matches AboutPage.jsx's TIMELINE array.
const HOME_HERITAGE_TIMELINE = [
  { year: "1998", slug: "kasbah-hotel-tombouctou", label: "Kasbah Hotel Tombouctou", hotelSlug: "kasbah-hotel-tombouctou" },
  { year: "2003", slug: "kasbah-hotel-xaluca-arfoud", label: "Kasbah Hotel Xaluca (Arfoud)", hotelSlug: "kasbah-hotel-xaluca-arfoud" },
  { year: "2008", slug: "auberge-la-belle-etoile", label: "Auberge La Belle Étoile", hotelSlug: "auberge-la-belle-etoile" },
  { year: "2011", slug: "hotel-xaluca-dades", label: "Hotel Xaluca Dades", hotelSlug: "hotel-xaluca-dades" },
  { year: "2014", slug: "xaluca-ifess-fess-refuge", label: "Xaluca Ifess Fess Refuge de Montagne", hotelSlug: "xaluca-ifess-fess-refuge" },
  { year: "2016", slug: "arfoud-voyages", label: "Arfoud Voyages", hotelSlug: null },
  { year: "2017", slug: "xaluca-tours", label: "Xaluca Tours (Sabadell)", hotelSlug: null },
  { year: "2020", slug: "xaluca-ait-ben-haddou", label: "Xaluca Aït Ben Haddou Maison d'Hôte", hotelSlug: "xaluca-ait-ben-haddou" },
  { year: "2023", slug: "hotel-xaluca-spa-aguelmame-sidi-ali", label: "Hotel Xaluca Spa Aguelmame Sidi Ali", hotelSlug: "hotel-xaluca-spa-aguelmame-sidi-ali" },
  { year: "2024", slug: "hotel-riad-xaluca", label: "Riad Xaluca Merzouga", hotelSlug: "hotel-riad-xaluca" },
  { year: "Soon", slug: "hotel-xaluca-dunes", label: "Hotel Xaluca Dunes", hotelSlug: "hotel-xaluca-dunes" },
];
HOME_HERITAGE_TIMELINE.forEach(({ year, slug, label, hotelSlug }) => {
  STATIC_SLOTS.push({
    slot: `cms.heritage-timeline.${year}-${slug}.card.image`,
    page: "/",
    section: "Home — Heritage Timeline carousel",
    label: `Heritage — ${year} · ${label}`,
    description: `Home page heritage-timeline card photo for the ${year} milestone (“${label}”). Falls back to the matching hotel hero when no per-card override is set.`,
    fallback: null,
    fallbackKind: hotelSlug ? "brand" : "external",
    hotelSlug: hotelSlug || undefined,
  });
});

// Experiences — cinematic Bespoke Experiences section hero background.
// Renders inside <BespokeExperiencesSection> with data-cms-scope
// "bespoke-experiences.hero" + data-cms-role="bg", so its slot id is
// stable regardless of DOM refactors and future page rearrangement.
STATIC_SLOTS.push({
  slot: "cms.bespoke-experiences.hero.bg",
  page: "/experiences",
  section: "Experiences — Bespoke Experiences hero",
  label: "Bespoke Experiences — hero background",
  description: "Full-bleed cinematic backdrop behind the “Bespoke Experiences” form on /experiences. aria-hidden decorative image; edit to change the mood shot.",
  fallback: null,
  fallbackKind: "external",
});

// Spa & Wellness feature block — one hero photo per property that
// receives the full spa treatment (5 hotels). Rendered by
// <SpaWellnessFeature scope="hotel.<slug>.spa-wellness"> with
// data-cms-role="hero", so slot id is
// `cms.hotel.<slug>.spa-wellness.hero`.
const SPA_WELLNESS_HOTELS = [
  { slug: "kasbah-hotel-xaluca-arfoud",           label: "Kasbah Hotel Xaluca (Arfoud)" },
  { slug: "kasbah-hotel-tombouctou",              label: "Kasbah Hotel Tombouctou" },
  { slug: "hotel-riad-xaluca",                    label: "Riad Xaluca Merzouga" },
  { slug: "hotel-xaluca-dades",                   label: "Hotel Xaluca Dades" },
  { slug: "hotel-xaluca-spa-aguelmame-sidi-ali",  label: "Hotel Xaluca Spa Aguelmame Sidi Ali" },
];
SPA_WELLNESS_HOTELS.forEach(({ slug, label }) => {
  STATIC_SLOTS.push({
    slot: `cms.hotel.${slug}.spa-wellness.hero`,
    page: `/hotels/${slug}`,
    section: `${label} — Spa & Wellness feature`,
    label: `${label} — Spa & Wellness hero`,
    description: `Immersive editorial photograph anchoring the “Spa & Wellness” section on /hotels/${slug}. Replaces the treatment-room, hammam or ritual close-up used as the section's story starter.`,
    fallback: null,
    fallbackKind: "brand",
    hotelSlug: slug,
  });
});


// Experiences — 4 flagship + 5 secondary cards
const EXPERIENCE_KEYS = ["desert", "atlas", "kasbah", "berber", "sunrise-camel-trek", "sunset-camel-trek", "arfoud-pizza-night", "quad-erg-chebbi-2h", "quad-erg-chebbi-4h"];
EXPERIENCE_KEYS.forEach((key) => {
  STATIC_SLOTS.push({
    slot: `cms.experiences.flagship.${key}.card.image`,
    page: "/experiences",
    section: "Experiences — cards",
    label: `Experience card — ${key}`,
    description: `4:3 card image for the experience “${key}” on the Experiences index.`,
    fallback: null,
  });
});

// Experience detail — hero + about gallery (3 tiles)
EXPERIENCE_KEYS.forEach((key) => {
  STATIC_SLOTS.push({
    slot: `cms.experience-detail.${key}.hero`,
    page: `/experiences/${key}`,
    section: "Experience detail — hero",
    label: `${key} — detail hero`,
    description: `Full-bleed hero on the dedicated experience page for “${key}”.`,
    fallback: null,
  });
  for (let i = 0; i < 3; i++) {
    STATIC_SLOTS.push({
      slot: `cms.experience-detail.${key}.gallery.${i}`,
      page: `/experiences/${key}`,
      section: "Experience detail — gallery",
      label: `${key} — gallery ${i + 1}`,
      description: `About-block gallery tile #${i + 1} on the experience page “${key}”.`,
      fallback: null,
    });
  }
});

// Booking heroes (single + multi + main)
[
  { slot: "cms.booking-single.hero.bg", page: "/booking/single", section: "Booking single — hero", label: "Booking single — hero", description: "52vh hero at the top of the single-property booking flow." },
  { slot: "cms.booking-multi.hero.bg",  page: "/booking/multi",  section: "Booking multi — hero",  label: "Booking multi — hero",  description: "52vh hero at the top of the multi-property booking flow." },
].forEach((s) => STATIC_SLOTS.push({ ...s, fallback: null }));

// Booking main page — per-hotel card thumb
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.booking-hotel-card.${h.slug}.thumb`,
    page: "/booking",
    section: "Booking — hotel selector",
    label: `${h.name} — booking thumb`,
    description: `4:3 thumb on the hotel selector grid of the main booking page.`,
    fallback: h.image,
  });
});

// Contact — per-hotel card thumb
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.contact-property.${h.slug}.thumb`,
    page: "/contact",
    section: "Contact — property tiles",
    label: `${h.name} — contact thumb`,
    description: `4:3 thumb on the property grid of the Contact page (also drives the “write to this property” block).`,
    fallback: h.image,
  });
});

// Pricing — per-hotel card thumb
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.pricing-property.${h.slug}.thumb`,
    page: "/pricing",
    section: "Pricing — property tiles",
    label: `${h.name} — pricing thumb`,
    description: `Square thumb next to the price table on the Pricing page for ${h.name}.`,
    fallback: h.image,
  });
});

// Packs — 8 pack covers
const PACK_SLUGS = ["desert-soul-erg-chebbi", "atlas-refuge", "kasbah-heritage", "honeymoon-sahara", "family-adventure", "photographers-journey", "stargazing-and-silence", "gastronomic-south"];
PACK_SLUGS.forEach((slug) => {
  STATIC_SLOTS.push({
    slot: `cms.pack-card.${slug}.card.image`,
    page: "/packs",
    section: "Packs — covers",
    label: `Pack cover — ${slug}`,
    description: `5:4 / 16:11 cover image for the pack card “${slug}”.`,
    fallback: null,
  });
});

// Instagram strip — 9 tiles
for (let i = 0; i < 9; i++) {
  STATIC_SLOTS.push({
    slot: `cms.instagram.post.${i}.image`,
    page: "(global)",
    section: "Instagram strip",
    label: `Instagram tile #${i + 1}`,
    description: "Tile in the Instagram editorial grid at the bottom of the homepage. Editor controls the photo and caption.",
    fallback: null,
  });
}

// Homepage — refuge collection hero + bivouacs collection hero + camp chips
STATIC_SLOTS.push({
  slot: "cms.bivouacs-collection.hero",
  page: "/",
  section: "Homepage — bivouacs collection",
  label: "Bivouacs collection — hero",
  description: "80vh full-bleed hero for the “Bivouacs & Desert Camps” block on the homepage.",
  fallback: null,
});
HOTELS.filter((h) => h.category === "refuge").forEach((r) => {
  STATIC_SLOTS.push({
    slot: `cms.refuge-collection.${r.slug}.hero`,
    page: "/",
    section: "Homepage — refuge collection",
    label: `${r.name} — refuge hero`,
    description: `Full-bleed hero on the “Refuge & Mountain Lodge” block on the homepage (per-refuge override).`,
    fallback: r.image,
  });
});
HOTELS.filter((h) => h.category === "bivouac").forEach((b) => {
  STATIC_SLOTS.push({
    slot: `cms.bivouacs-camp-chip.${b.slug}.thumb`,
    page: "/",
    section: "Homepage — camp chips",
    label: `${b.name} — camp chip thumb`,
    description: `12×12 thumbnail rendered next to the camp name in the bivouacs collection chip row on the homepage.`,
    fallback: b.image,
  });
});

// Homepage — experience cards (4 flagship) and editorial sections
EXPERIENCE_KEYS.slice(0, 4).forEach((key) => {
  STATIC_SLOTS.push({
    slot: `cms.home.experiences.${key}.card`,
    page: "/",
    section: "Homepage — experience cards",
    label: `Home experience card — ${key}`,
    description: `4:3 card image for the experience “${key}” in the Cultural Adventures grid on the homepage.`,
    fallback: null,
  });
});
STATIC_SLOTS.push({
  slot: "cms.home.originals.backdrop",
  page: "/",
  section: "Homepage — Xaluca Originals",
  label: "Xaluca Originals — backdrop",
  description: "Immersive backdrop behind the secondary Xaluca Originals panel on the homepage.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.home.heritage.feature",
  page: "/",
  section: "Homepage — heritage",
  label: "Heritage feature image",
  description: "60vh feature photo paired with the heritage block on the homepage.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.home.events.hero",
  page: "/",
  section: "Homepage — events block",
  label: "Events block — hero",
  description: "Full-width 60-70vh image behind the Events block on the homepage.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.home.bivouacs.dunes-bg",
  page: "/",
  section: "Homepage — bivouacs block",
  label: "Bivouacs block — dunes backdrop",
  description: "Transparent dunes silhouette anchored to the bottom of the bivouacs block.",
  fallback: null,
});
// Gastronomy gallery dish slots (5 + thumb pairs)
for (let i = 0; i < 6; i++) {
  STATIC_SLOTS.push({
    slot: `cms.home.gastronomy.gallery.dish.${i}`,
    page: "/",
    section: "Homepage — gastronomy gallery",
    label: `Gastronomy dish #${i + 1}`,
    description: `Dish frame #${i + 1} in the gastronomy cross-fade gallery on the homepage (used both as full image AND thumb).`,
    fallback: null,
  });
}

// Discovery section — Xaluca logo + dunes
STATIC_SLOTS.push({
  slot: "cms.home.discovery.logo",
  page: "/",
  section: "Homepage — discovery section",
  label: "Discovery logo",
  description: "Xaluca wordmark centered above the “Start here” discovery cards on the homepage.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.home.discovery.dunes",
  page: "/",
  section: "Homepage — discovery section",
  label: "Discovery dunes",
  description: "Dunes silhouette anchored at the bottom of the discovery section.",
  fallback: null,
});

// VideoSection — used on hotel and bivouac detail pages
STATIC_SLOTS.push({
  slot: "cms.hotel-videos.video.featured.poster",
  page: "/hotels/*",
  section: "Hotel videos",
  label: "Hotel video — featured poster",
  description: "Poster image used as the featured-video thumbnail in the “Hotel videos” block. Stable slot across every hotel detail page when the video object id is `featured`.",
  fallback: null,
});

// Global brand surfaces — header, drawer, footer, mark
STATIC_SLOTS.push({
  slot: "cms.global.brand.logo",
  page: "(global)",
  section: "Brand",
  label: "Brand — primary logo",
  description: "The Grup Xaluca wordmark used in the Header (and drawer when open).",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.global.footer.logo",
  page: "(global)",
  section: "Footer",
  label: "Footer — circular logo",
  description: "Circular Xaluca logo rendered in the global footer brand block.",
  fallback: null,
});
STATIC_SLOTS.push({
  slot: "cms.global.brand.mark",
  page: "(global)",
  section: "Brand",
  label: "Brand — corner mark",
  description: "Small Xaluca mark rendered in the corner of editorial photos via the <BrandMark> helper.",
  fallback: null,
});

// ── Fase 4 (iter 29) — site-wide route coverage ────────────────────────
//
// Routes registered in /app/frontend/src/App.js that previously had no
// directory presence — now declared here so /admin/images mirrors the
// entire public route table.

// /hotels — index page using <PropertyCardGallery> for each hotel.
// PropertyCardGallery uses the per-position `cms.hotel.<slug>.thumb.<i>`
// slots and the per-hotel "card" section in /admin/galleries, so the
// index page surfaces those alongside its own `hotels-index` card.
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `cms.hotels-index.${h.slug}.card.image`,
    page: "/hotels",
    section: "Hotels index — cards",
    label: `${h.name} — hotels index card`,
    description: `Card image rendered on the /hotels index page for ${h.name}. Falls back to the per-hotel "card" gallery in /admin/galleries.`,
    fallback: h.image,
    relatedAdmin: { href: "/admin/galleries", label: "Manage card thumbnails in /admin/galleries" },
  });
});

// /classic-booking & /booking-classic — legacy booking flow.
STATIC_SLOTS.push({
  slot: "cms.classic-booking.hero.bg",
  page: "/classic-booking",
  section: "Classic booking — hero",
  label: "Classic booking — hero",
  description: "Aerial Kasbah backdrop on the legacy /classic-booking flow (also reached via /booking-classic).",
  fallback: null,
});

// /hotels/<slug>/services/<service> — ServiceDetailPage. The page emits
// per-pair scopes (`service.<hotelSlug>.<serviceSlug>`) so slots resolve
// lazily as editors visit each combination. We surface placeholder rows
// per hotel that point to the canonical service slugs the codebase
// exposes (rooms, spa-wellness, gastronomy/bars-restaurants, kasbah).
//
// The runtime also generates gallery thumbnails (up to ~12 per service
// page). We enumerate them explicitly so every gallery tile is
// individually addressable from /admin/images.
const COMMON_SERVICE_SLUGS = ["rooms", "spa-wellness", "spa-hammam", "bars-restaurants", "restaurants-bars", "gastronomy", "kasbah", "events", "wellness"];
HOTELS.forEach((h) => {
  COMMON_SERVICE_SLUGS.forEach((svc) => {
    STATIC_SLOTS.push({
      slot: `cms.service.${h.slug}.${svc}.hero`,
      page: `/hotels/${h.slug}/services/${svc}`,
      section: "Service detail — hero",
      label: `${h.name} — ${svc} hero`,
      description: `Full-bleed hero on /hotels/${h.slug}/services/${svc}. Falls back to the service's default photo from hotelDetails.js until customised.`,
      fallback: null,
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
    // 12 gallery thumbnails per service page — covers the typical
    // "12 photos" gallery the site renders on every service detail.
    for (let i = 0; i < 12; i += 1) {
      STATIC_SLOTS.push({
        slot: `cms.service.${h.slug}.${svc}.gallery.thumb.${i}`,
        page: `/hotels/${h.slug}/services/${svc}`,
        section: `Service detail — ${svc} gallery`,
        label: `${h.name} — ${svc} gallery #${i + 1}`,
        description: `Photo ${i + 1} in the /hotels/${h.slug}/services/${svc} gallery grid. Falls back to hotelDetails.js defaults.`,
        fallback: null,
        fallbackKind: "external",
        hotelSlug: h.slug,
      });
    }
  });
});

// /hotels/<slug>/excursions/<idx> + /hotels/<slug>/nearby/<idx> —
// ActivityDetailPage emits `activity.<hotelSlug>.<type>.<index>` scopes
// with `hero`, `card`, and `gallery.tile.<i>` roles. Number of
// activities per hotel varies (usually 4–8) so we enumerate up to 10
// per hotel per type. Each activity has one hero + one card thumb +
// up to 6 gallery tiles.
HOTELS.forEach((h) => {
  ["excursions", "nearby"].forEach((type) => {
    for (let idx = 0; idx < 10; idx += 1) {
      // Hero image at the top of the activity page.
      STATIC_SLOTS.push({
        slot: `cms.activity.${h.slug}.${type}.${idx}.hero`,
        page: `/hotels/${h.slug}/${type}/<slug>`,
        section: `Activity — ${type} · Hero`,
        label: `${h.name} — ${type} #${idx + 1} · Hero`,
        description: `Full-bleed hero on /hotels/${h.slug}/${type}/<slug>. Applies to whichever activity is at position ${idx + 1} in the hotel's ${type} list.`,
        fallback: null,
        fallbackKind: "external",
        hotelSlug: h.slug,
      });
      // Thumbnail on the activity card rendered inside the hotel
      // detail page's ${type} grid.
      STATIC_SLOTS.push({
        slot: `cms.activity.${h.slug}.${type}.${idx}.card.image`,
        page: `/hotels/${h.slug}/${type}/<slug>`,
        section: `Activity — ${type} · Card`,
        label: `${h.name} — ${type} #${idx + 1} · Card thumb`,
        description: `4:3 card thumbnail shown inside the /hotels/${h.slug} ${type} grid for activity #${idx + 1}.`,
        fallback: null,
        fallbackKind: "external",
        hotelSlug: h.slug,
      });
      // Up to 6 gallery tiles per activity.
      for (let tile = 0; tile < 6; tile += 1) {
        STATIC_SLOTS.push({
          slot: `cms.activity.${h.slug}.${type}.${idx}.gallery.tile.${tile}`,
          page: `/hotels/${h.slug}/${type}/<slug>`,
          section: `Activity — ${type} · Gallery`,
          label: `${h.name} — ${type} #${idx + 1} · Gallery tile ${tile + 1}`,
          description: `Photo ${tile + 1} in the gallery of the activity page. Displayed as part of the swipeable gallery under the hero.`,
          fallback: null,
          fallbackKind: "external",
          hotelSlug: h.slug,
        });
      }
    }
  });
});

// Property films — every hotel detail page renders a VideoSection with
// 5 to 7 short films (`feat`, `bivouacs`, `gastronomy`, `experiences`,
// `heritage`, `wellness`, `stories`). Runtime slot pattern:
// `cms.hotel-<slug>-videos.video.<video-id>.poster`.
const HOTEL_VIDEO_IDS = ["feat", "bivouacs", "gastronomy", "experiences", "heritage", "wellness", "storytelling"];
HOTELS.forEach((h) => {
  HOTEL_VIDEO_IDS.forEach((vid) => {
    STATIC_SLOTS.push({
      slot: `cms.hotel-${h.slug}-videos.video.hotel-${h.slug}-${vid}.poster`,
      page: `/hotels/${h.slug}`,
      section: "Hotel detail — Property films",
      label: `${h.name} — Property film · ${vid} poster`,
      description: `Poster of the "${vid}" short film in the Property films section of /hotels/${h.slug}. Editor can also set a video URL; the poster shows while loading.`,
      fallback: null,
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
  });
});

// About-gallery tiles — every hotel detail page's About section
// includes a 5-tile editorial photo grid. Runtime pattern:
// `cms.hotel.<slug>.about-gallery.tile.<i>`.
HOTELS.forEach((h) => {
  for (let i = 0; i < 5; i += 1) {
    STATIC_SLOTS.push({
      slot: `cms.hotel.${h.slug}.about-gallery.tile.${i}`,
      page: `/hotels/${h.slug}`,
      section: "Hotel detail — About gallery",
      label: `${h.name} — About gallery tile ${i + 1}`,
      description: `Tile ${i + 1} of the 5-tile editorial photo grid inside the About section on /hotels/${h.slug}.`,
      fallback: null,
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
  }
});

// Hotel property card thumbnails — visible on /hotels (index) and on
// the collection strips of the homepage. Runtime patterns:
// `cms.hotel.<slug>.card.N` and `cms.hotel.<slug>.thumb.N`.
HOTELS.forEach((h) => {
  for (let i = 0; i < 5; i += 1) {
    STATIC_SLOTS.push({
      slot: `cms.hotel.${h.slug}.card.${i}`,
      page: "/hotels",
      section: "Hotels index — Property cards",
      label: `${h.name} — property card · image ${i + 1}`,
      description: `Photo ${i + 1} of the property card carousel on /hotels for ${h.name}.`,
      fallback: null,
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
    STATIC_SLOTS.push({
      slot: `cms.hotel.${h.slug}.thumb.${i}`,
      page: "/hotels",
      section: "Hotels index — Card thumbnails",
      label: `${h.name} — card thumbnail ${i + 1}`,
      description: `Thumbnail ${i + 1} on the compact ${h.name} card on /hotels and related index pages.`,
      fallback: null,
      fallbackKind: "external",
      hotelSlug: h.slug,
    });
  }
});

// Booking widget property thumbnails — the booking hub shows a picker
// with a small photo per property. Runtime slot pattern:
// `booking-widget.thumb.<hotelSlug>`.
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `booking-widget.thumb.${h.slug}`,
    page: "/booking",
    section: "Booking widget — Property picker",
    label: `Booking picker thumbnail — ${h.name}`,
    description: `Thumbnail of ${h.name} rendered inside the property picker of the /booking flow (Multi and Single).`,
    fallback: null,
    fallbackKind: "external",
    hotelSlug: h.slug,
  });
});

// Homepage sub-section image slots not yet declared above.
// Runtime patterns observed in the production DB.
const HOME_SUB_SECTION_SLOTS = [
  { slot: "cms.home.originals.image",   section: "Homepage — Originals bg",        label: "Homepage — Xaluca Originals section background" },
  { slot: "cms.home.discovery.image",   section: "Homepage — Discovery bg",         label: "Homepage — Discover the south section background" },
  { slot: "cms.home.events.hero",       section: "Homepage — Events strip",        label: "Homepage — Events strip hero image" },
  { slot: "cms.home.hero.image",        section: "Homepage — Hero fallback (legacy)", label: "Homepage — Hero fallback (single-image mode)" },
];
HOME_SUB_SECTION_SLOTS.forEach((s) => {
  STATIC_SLOTS.push({
    slot: s.slot,
    page: "/",
    section: s.section,
    label: s.label,
    description: `Editorial image rendered on the homepage. Slot id matches the runtime AutoImageEditor output — replace via /admin/images or in-page Edit Mode.`,
    fallback: null,
    fallbackKind: "external",
  });
});

// Homepage — Heritage polaroid gallery (10 default polaroids +
// 4 heritage-team members). Runtime: `cms.home.heritage.polaroids
// .polaroid.<id>` + `cms.home.heritage.gallery.heritage.<i>`.
const HERITAGE_POLAROID_IDS = ["default-family", "default-kasbah", "default-tea", "default-oud", "default-cook", "default-dance", "default-camel", "default-wedding", "default-carpet", "default-berber"];
HERITAGE_POLAROID_IDS.forEach((id) => {
  STATIC_SLOTS.push({
    slot: `cms.home.heritage.polaroids.polaroid.${id}`,
    page: "/",
    section: "Homepage — Heritage polaroids",
    label: `Heritage polaroid — ${id.replace("default-", "")}`,
    description: `Polaroid photo on the homepage Heritage section (${id.replace("default-", "")}).`,
    fallback: null,
    fallbackKind: "external",
  });
});
// Heritage carousel photos beyond the initial one (indexes 1-9).
for (let i = 1; i <= 9; i += 1) {
  STATIC_SLOTS.push({
    slot: `cms.home.heritage.gallery.heritage.${i}`,
    page: "/",
    section: "Homepage — Heritage carousel",
    label: `Heritage carousel — photo ${i + 1}`,
    description: `Photo ${i + 1} in the "Our Heritage" carousel on the homepage.`,
    fallback: null,
    fallbackKind: "external",
  });
}

// Homepage — Gastronomy strip (8 dish tiles).
for (let i = 0; i < 8; i += 1) {
  STATIC_SLOTS.push({
    slot: `cms.home.gastronomy.dish.${i}`,
    page: "/",
    section: "Homepage — Gastronomy dishes",
    label: `Homepage — Dish ${i + 1}`,
    description: `Dish ${i + 1} of the homepage Gastronomy strip.`,
    fallback: null,
    fallbackKind: "external",
  });
}

// Homepage — Experiences highlight tiles (desert, gastronomy, wellness, culture, adventure).
const HOME_EXPERIENCE_KEYS = ["desert", "gastronomy", "wellness", "culture", "adventure"];
HOME_EXPERIENCE_KEYS.forEach((k) => {
  STATIC_SLOTS.push({
    slot: `cms.home.experiences.${k}.card`,
    page: "/",
    section: "Homepage — Experiences highlights",
    label: `Homepage — Experience card · ${k}`,
    description: `Highlight card for the "${k}" experience family on the homepage.`,
    fallback: null,
    fallbackKind: "external",
  });
});

// Homepage — Collection cards (per hotel — image behind the property
// card on the "Our Collection" strip).
HOTELS.forEach((h) => {
  STATIC_SLOTS.push({
    slot: `home.collection.${h.slug}.image`,
    page: "/",
    section: "Homepage — Our Collection",
    label: `Homepage — Collection card · ${h.name}`,
    description: `Portrait photo behind the ${h.name} card on the "Our Collection" strip of the homepage.`,
    fallback: null,
    fallbackKind: "external",
    hotelSlug: h.slug,
  });
});

// /experiences — Flagship experience cards (5 keys mirroring the
// XALUCA_ORIGINAL_KEYS but on the dedicated Experiences page).
const EXPERIENCE_FLAGSHIP_KEYS = ["desert", "gastronomy", "wellness", "culture", "adventure"];
EXPERIENCE_FLAGSHIP_KEYS.forEach((k) => {
  STATIC_SLOTS.push({
    slot: `cms.experiences.flagship.${k}.card.image`,
    page: "/experiences",
    section: "Experiences — Flagship cards",
    label: `Experiences — Flagship card · ${k}`,
    description: `4:3 flagship card for the "${k}" experience family on the /experiences page.`,
    fallback: null,
    fallbackKind: "external",
  });
});

// Instagram embed cards — homepage social proof.
for (let i = 1; i <= 9; i += 1) {
  STATIC_SLOTS.push({
    slot: `cms.instagram.post.${i}.image`,
    page: "/",
    section: "Homepage — Instagram feed",
    label: `Instagram post ${i}`,
    description: `Post ${i} in the Instagram feed strip on the homepage.`,
    fallback: null,
    fallbackKind: "external",
  });
}

// Collection landing heroes — the specialised page-strip carousels
// (bivouacs, refuge, kasbah, riad) rendered as scoped surfaces from
// several pages.
const COLLECTION_HERO_SLOTS = [
  { slot: "cms.bivouacs-collection.hero", page: "/bivouacs", section: "Bivouacs collection — Hero",
    label: "Bivouacs collection — Hero image" },
  { slot: "cms.refuge-collection.xaluca-ifess-fess-refuge.hero", page: "/hotels", section: "Refuge collection — Hero",
    label: "Refuge & Mountain Lodge — collection hero" },
];
COLLECTION_HERO_SLOTS.forEach((c) => {
  STATIC_SLOTS.push({
    slot: c.slot,
    page: c.page,
    section: c.section,
    label: c.label,
    description: `Hero image for the ${c.label.toLowerCase()} on ${c.page}.`,
    fallback: null,
    fallbackKind: "external",
  });
});


// /knowledgebase — KnowledgeBasePage. No standalone image slots
// today; it relies on the global brand surfaces. We add a single
// info slot so the page appears in the directory.
STATIC_SLOTS.push({
  slot: "cms.knowledgebase.hero.bg",
  page: "/knowledgebase",
  section: "Knowledge base — hero",
  label: "Knowledge base — hero",
  description: "Reserved slot for the Knowledge base landing hero. Currently rendered as a typography-first layout; upload here once a hero image is desired.",
  fallback: null,
  pending: true,
});

// ── 7. Future slots — not yet wired in components ─────────────────────
//
// These describe the slot ids editors will see on /admin/images even
// before the corresponding hardcoded image has been migrated. Once
// the migration lands the component starts honouring them — until then
// they sit harmlessly in the directory marked "pending".
//
// Currently empty — fase 3 migrated every remaining hardcoded surface
// into proper STATIC_SLOTS above. New PENDING entries should be added
// here ONLY when we know a future component will need a slot but the
// code isn't written yet.
const PENDING_SLOTS = [];

// Post-process every slot to attach a `hotelSlug` (derived from the slot
// id) when not explicitly set. This powers the per-hotel filter in
// /admin/images so each of the 14 hotels gets its own dashboard.
const _ALL = [...STATIC_SLOTS, ...PENDING_SLOTS];
_ALL.forEach((s) => {
  if (!s.hotelSlug) {
    const derived = deriveHotelSlug(s.slot);
    if (derived) s.hotelSlug = derived;
  }
});

export const IMAGE_SLOT_REGISTRY = _ALL;

// Ordered list of all hotels for the per-hotel sidebar in /admin/images.
export const HOTEL_OPTIONS = HOTELS.map((h) => ({
  slug: h.slug,
  name: h.name,
  category: h.category,
}));

// ── 6. Page metadata for the directory sidebar ─────────────────────────
//
// Every public route is listed here so /admin/images displays the full
// site map even when a page only renders already-managed components
// (e.g. /hotels renders <PropertyCardGallery> whose slots live under
// the homepage collection cards). Pages that have no slots of their
// own still show up with a count of zero — clicking them surfaces the
// related downstream slots via the deep-link column on each row.
export const PAGE_LABELS = {
  "/":                   { label: "Homepage",                emoji: "🏡" },
  "/about":              { label: "About",                   emoji: "📖" },
  "/contact":            { label: "Contact",                 emoji: "✉️" },
  "/booking":            { label: "Booking",                 emoji: "📅" },
  "/booking/single":     { label: "Booking — single",        emoji: "📅" },
  "/booking/multi":      { label: "Booking — multi",         emoji: "📅" },
  "/classic-booking":    { label: "Booking — classic (legacy)", emoji: "📅" },
  "/hotels":             { label: "Hotels — index",          emoji: "🛏" },
  "/bivouacs":           { label: "Bivouacs",                emoji: "🏕" },
  "/experiences":        { label: "Experiences",             emoji: "🗺" },
  "/events":             { label: "Events",                  emoji: "🎼" },
  "/gastronomy":         { label: "Gastronomy",              emoji: "🍽" },
  "/southern-morocco":   { label: "Southern Morocco",        emoji: "🌅" },
  "/trips":              { label: "Trips",                   emoji: "🚌" },
  "/press":              { label: "Press & enquiries",       emoji: "🗞" },
  "/blog":               { label: "Blog",                    emoji: "🪶" },
  "/wall-of-love":       { label: "Wall of love",            emoji: "💌" },
  "/work-with-us":       { label: "Work with us",            emoji: "🤝" },
  "/partners":           { label: "Partners",                emoji: "🤝" },
  "/pricing":            { label: "Pricing",                 emoji: "🏷" },
  "/packs":              { label: "Packs",                   emoji: "🎁" },
  "/directions":         { label: "Directions & arrival",    emoji: "🧭" },
  "/knowledgebase":      { label: "Knowledge base",          emoji: "📚" },
  "(global)":            { label: "Global (Header / Footer)", emoji: "🌐" },
};

// Hotel detail pages collapse onto a single bucket with a count so the
// sidebar doesn't list 14 separate hotel rows. Same trick for the
// per-experience detail pages.
export const groupPage = (page) => {
  if (page && page.startsWith("/hotels/")) return "/hotels/*";
  if (page && page.startsWith("/experiences/")) return "/experiences/*";
  return page || "(other)";
};

// Sidebar key for the per-hotel dashboard. We prefix with "hotel:" so the
// filter dispatcher in /admin/images can tell hotel filters apart from
// page filters at a glance.
export const hotelKey = (slug) => `hotel:${slug}`;
export const isHotelKey = (key) => typeof key === "string" && key.startsWith("hotel:");
export const hotelSlugFromKey = (key) => (isHotelKey(key) ? key.slice("hotel:".length) : null);

export const groupLabel = (key) => {
  if (key === "/hotels/*") return { label: "Hotels — detail pages", emoji: "🛏" };
  if (key === "/experiences/*") return { label: "Experience detail pages", emoji: "🗺" };
  if (key === "(other)") return { label: "Other (unregistered)", emoji: "📦" };
  if (isHotelKey(key)) {
    const slug = hotelSlugFromKey(key);
    const h = hotelBySlug(slug);
    return { label: h ? h.name : slug, emoji: "🛏" };
  }
  return PAGE_LABELS[key] || { label: key, emoji: "📄" };
};

// Look up a registry entry by slot id (used to enrich rows fetched from
// the API with their declared page/section/label metadata).
const SLOT_INDEX = new Map(IMAGE_SLOT_REGISTRY.map((s) => [s.slot, s]));
export const lookupSlot = (slotId) => SLOT_INDEX.get(slotId) || null;

// Pretty-print a hotel slug for the "Unregistered" detail row.
export const prettyHotelName = (slug) => {
  const h = hotelBySlug(slug);
  return h ? h.name : slug;
};
